import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../api/axiosConfig';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user,    setUser]    = useState(null);
    const [loading, setLoading] = useState(true);

    // ── Extract minimal data from JWT for immediate use ───────────────────
    // NOTE: JWT payload only contains: { id, uuid, company_id, role_id, role }
    // first_name, last_name etc are NOT in the token — they come from /auth/me
    const getUserFromToken = (token) => {
        try {
            const decoded = jwtDecode(token);
            return {
                id:         decoded.id,
                uuid:       decoded.uuid,
                role:       decoded.role,        // already a slug e.g. 'super-admin'
                role_id:    decoded.role_id,
                company_id: decoded.company_id,
                // Placeholders — will be overwritten by /auth/me response
                first_name:   '',
                last_name:    '',
                email:        '',
                company_name: '',
                logo_url:     null,
                primary_color: '#4f46e5',
            };
        } catch {
            return null;
        }
    };

    // ── Check for existing session on app load ─────────────────────────────
    useEffect(() => {
        const checkLoggedIn = async () => {
            const token = localStorage.getItem('token');

            if (!token) { setLoading(false); return; }

            // Set minimal user from token immediately so PrivateRoute doesn't redirect
            const tokenUser = getUserFromToken(token);
            if (tokenUser) setUser(tokenUser);

            try {
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const res = await axiosInstance.get('/auth/me');

                if (res.data.success) {
                    // /auth/me returns full user including first_name, company_name, logo_url, role
                    const meData = res.data.data;
                    // Ensure role slug is set (getMe returns role field as slug)
                    setUser({
                        ...meData,
                        role: meData.role || tokenUser?.role,
                    });
                } else {
                    throw new Error('Invalid session');
                }
            } catch (err) {
                if (err.response?.status === 401) logout();
            } finally {
                setLoading(false);
            }
        };

        checkLoggedIn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Login ─────────────────────────────────────────────────────────────
    const login = (token, userData) => {
        return new Promise((resolve) => {
            localStorage.setItem('token', token);
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            // userData comes from login response — has first_name, role, company_name etc
            setUser(userData || getUserFromToken(token));
            setTimeout(() => resolve(true), 50);
        });
    };

    // ── Logout ────────────────────────────────────────────────────────────
    const logout = () => {
        localStorage.removeItem('token');
        delete axiosInstance.defaults.headers.common['Authorization'];
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
